import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { cloneDefaultOutline } from './defaults';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * useSermonDrafts — encapsulates the drafts list, revisions, load/delete/revert
 * flows. Keeps the main component free of network+toast plumbing.
 */
export default function useSermonDrafts({
  t,
  setSermonTopic,
  setSermonNotes,
  setOutline,
  setResearchResults,
  setDraftId,
  draftId,
  setDraftsOpen,
  setSeriesId,
  setSeriesPosition,
  setTags,
}) {
  const [draftsList, setDraftsList] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [revisionsForId, setRevisionsForId] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [loadingRevisions, setLoadingRevisions] = useState(false);

  const fetchDrafts = async () => {
    setLoadingDrafts(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/sermons/drafts`, {
        withCredentials: true,
      });
      setDraftsList(res.data.drafts || []);
    } catch (err) {
      console.warn('Failed to load drafts:', err);
      toast.error(t('sermon.failLoadDrafts'));
    } finally {
      setLoadingDrafts(false);
    }
  };

  const loadDraft = async (id) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/sermons/drafts/${id}`, {
        withCredentials: true,
      });
      const d = res.data;
      setSermonTopic(d.topic || '');
      setSermonNotes(d.notes || '');
      setOutline(
        Array.isArray(d.outline) && d.outline.length > 0
          ? d.outline
          : cloneDefaultOutline(),
      );
      setResearchResults(d.research || null);
      setSeriesId?.(d.series_id || null);
      setSeriesPosition?.(d.series_position ?? null);
      setTags?.(Array.isArray(d.tags) ? d.tags : []);
      setDraftId(id);
      setDraftsOpen(false);
      toast.success(t('sermon.draftLoaded'));
    } catch (err) {
      console.warn('Failed to load draft:', err);
      toast.error(t('sermon.failLoadDraft'));
    }
  };

  const deleteDraft = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/sermons/drafts/${id}`, {
        withCredentials: true,
      });
      setDraftsList((prev) => prev.filter((d) => d.draft_id !== id));
      if (id === draftId) setDraftId(null);
      toast.success(t('sermon.draftDeleted'));
    } catch (err) {
      console.warn('Failed to delete draft:', err);
      toast.error(t('sermon.failDeleteDraft'));
    }
  };

  const openRevisions = async (id) => {
    if (revisionsForId === id) {
      setRevisionsForId(null);
      setRevisions([]);
      return;
    }
    setRevisionsForId(id);
    setLoadingRevisions(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/sermons/drafts/${id}/revisions`,
        { withCredentials: true },
      );
      setRevisions(res.data.revisions || []);
    } catch (err) {
      console.warn('Failed to load revisions:', err);
      toast.error(t('sermon.failLoadRevisions'));
    } finally {
      setLoadingRevisions(false);
    }
  };

  const revertTo = async (id, revisionId) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/sermons/drafts/${id}/revert/${revisionId}`,
        {},
        { withCredentials: true },
      );
      const d = res.data;
      setSermonTopic(d.topic || '');
      setSermonNotes(d.notes || '');
      setOutline(
        Array.isArray(d.outline) && d.outline.length > 0
          ? d.outline
          : cloneDefaultOutline(),
      );
      setResearchResults(d.research || null);
      setSeriesId?.(d.series_id || null);
      setSeriesPosition?.(d.series_position ?? null);
      setTags?.(Array.isArray(d.tags) ? d.tags : []);
      setDraftId(id);
      const r = await axios.get(
        `${BACKEND_URL}/api/sermons/drafts/${id}/revisions`,
        { withCredentials: true },
      );
      setRevisions(r.data.revisions || []);
      setDraftsOpen(false);
      setRevisionsForId(null);
      toast.success(t('sermon.draftReverted'));
    } catch (err) {
      console.warn('Failed to revert:', err);
      toast.error(t('sermon.failRevert'));
    }
  };

  return {
    draftsList,
    loadingDrafts,
    revisionsForId,
    revisions,
    loadingRevisions,
    fetchDrafts,
    loadDraft,
    deleteDraft,
    openRevisions,
    revertTo,
  };
}
